import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BedDouble, Ruler, MapPin, Video, Building2, LayoutGrid } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { ViewingModal } from "@/components/ViewingModal";

const fmt = (n) => `AED ${Math.round(n).toLocaleString("en-US")}`;

const SliderRow = ({ label, value, min, max, step, onChange, display, testid }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs text-zinc-400">{label}</span>
      <span className="text-sm font-semibold text-gold-pale" data-testid={`${testid}-value`}>{display}</span>
    </div>
    <input
      type="range" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testid}
      className="w-full accent-[#D4AF37] cursor-pointer"
    />
  </div>
);

const MortgageCalculator = ({ price }) => {
  const [homePrice, setHomePrice] = useState(price);
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(4.49);
  const [years, setYears] = useState(25);

  const loan = homePrice * (1 - downPct / 100);
  const r = rate / 100 / 12;
  const n = years * 12;
  const monthly = r > 0 ? (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : loan / n;

  return (
    <div className="gold-ring rounded-3xl bg-ink-card p-7 sm:p-9" data-testid="mortgage-calculator">
      <h2 className="font-serif text-2xl text-[#F8F6F0]">Mortgage calculator</h2>
      <p className="mt-1.5 text-xs text-zinc-500">Indicative only — expats typically borrow up to 75–80% LTV on a first home.</p>
      <div className="mt-7 space-y-6">
        <SliderRow label="Property price" value={homePrice} min={500000} max={30000000} step={50000}
                   onChange={setHomePrice} display={fmt(homePrice)} testid="mortgage-price-input" />
        <SliderRow label="Down payment" value={downPct} min={15} max={80} step={5}
                   onChange={setDownPct} display={`${downPct}% (${fmt(homePrice * downPct / 100)})`} testid="mortgage-down-input" />
        <SliderRow label="Interest rate" value={rate} min={2} max={9} step={0.05}
                   onChange={setRate} display={`${rate.toFixed(2)}%`} testid="mortgage-rate-input" />
        <SliderRow label="Term" value={years} min={5} max={25} step={1}
                   onChange={setYears} display={`${years} years`} testid="mortgage-years-input" />
      </div>
      <div className="mt-8 pt-6 border-t border-gold/20 grid grid-cols-2 gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Loan amount</p>
          <p className="mt-1 font-serif text-xl text-[#F8F6F0]" data-testid="mortgage-loan-result">{fmt(loan)}</p>
        </div>
        <div className="text-right">
          <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">Monthly payment</p>
          <p className="mt-1 font-serif text-2xl gold-text" data-testid="mortgage-monthly-result">{fmt(monthly)}</p>
        </div>
      </div>
    </div>
  );
};

export default function ListingDetail() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [missing, setMissing] = useState(false);
  const [active, setActive] = useState(0);
  const [viewing, setViewing] = useState(false);

  useEffect(() => {
    setActive(0);
    api.get(`/listings/${id}`).then((r) => setListing(r.data)).catch(() => setMissing(true));
  }, [id]);

  const gallery = listing ? (listing.gallery?.length ? listing.gallery : [listing.image_url].filter(Boolean)) : [];

  return (
    <main className="bg-ink min-h-screen" data-testid="listing-detail-page">
      <Seo title={listing ? `${listing.title}, ${listing.community} — Haven & Hill Properties` : "Listing — Haven & Hill Properties"}
           description={listing?.description} path={`/listings/${id}`} />
      <Navbar />
      <div className="pt-32 pb-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/listings" data-testid="listing-back-link"
              className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-gold-light transition-colors">
          <ArrowLeft size={14} /> All listings
        </Link>

        {missing && <p className="mt-10 text-zinc-400">Listing not found.</p>}

        {listing && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="mt-8 grid lg:grid-cols-2 gap-10">
              <div>
                <div className="relative rounded-3xl overflow-hidden aspect-[4/3] gold-ring" data-testid="listing-gallery">
                  <img src={gallery[active]} alt={`${listing.title}, ${listing.community}`}
                       className="w-full h-full object-cover" />
                  <span className={`absolute top-4 left-4 rounded-full px-3 py-1 text-[10px] uppercase tracking-wider font-semibold ${
                    listing.status === "ready" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "glass text-gold-pale border border-gold/25"
                  }`}>{listing.status === "ready" ? "Ready" : "Off-plan"}</span>
                </div>
                {gallery.length > 1 && (
                  <div className="mt-4 grid grid-cols-3 gap-3" data-testid="listing-thumbs">
                    {gallery.map((g, i) => (
                      <button key={i} onClick={() => setActive(i)} data-testid={`listing-thumb-${i}`}
                              className={`rounded-xl overflow-hidden aspect-[4/3] border transition-all ${
                                i === active ? "border-gold/70" : "border-white/10 opacity-60 hover:opacity-100"
                              }`}>
                        <img src={g} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-gold-light/90 font-medium flex items-center gap-2">
                  <MapPin size={12} /> {listing.community}
                </p>
                <h1 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight text-[#F8F6F0] leading-tight" data-testid="listing-title">
                  {listing.title}
                </h1>
                <p className="mt-4 font-serif text-3xl gold-text" data-testid="listing-price">{listing.price_label}</p>

                <div className="mt-7 grid grid-cols-2 gap-3">
                  {[
                    { icon: BedDouble, label: "Bedrooms", value: listing.beds === 0 ? "Studio" : `${listing.beds}` },
                    { icon: Ruler, label: "Area", value: `${listing.area_sqft?.toLocaleString()} sqft` },
                    { icon: Building2, label: "Type", value: listing.type },
                    { icon: LayoutGrid, label: "Status", value: listing.status === "ready" ? "Ready to move" : "Off-plan" },
                  ].map((f) => (
                    <div key={f.label} className="gold-ring rounded-xl bg-ink-card/60 p-4">
                      <f.icon size={16} className="text-gold mb-2" />
                      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{f.label}</p>
                      <p className="text-sm text-[#F8F6F0] capitalize mt-0.5">{f.value}</p>
                    </div>
                  ))}
                </div>

                <p className="mt-7 text-sm sm:text-base text-zinc-300 leading-relaxed" data-testid="listing-description">
                  {listing.description}
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <button onClick={() => setViewing(true)} data-testid="listing-viewing-btn"
                          className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-ink hover:bg-gold-hover transition-colors">
                    <Video size={16} /> Request virtual viewing
                  </button>
                  <a href="/#booking" data-testid="listing-enquire-link"
                     className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-7 py-3.5 text-sm font-medium text-gold-pale hover:bg-gold/10 transition-colors">
                    Enquire about this property
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-16 grid lg:grid-cols-2 gap-6 items-start">
              <div className="gold-ring rounded-3xl bg-ink-card p-7 sm:p-9" data-testid="floor-plan-card">
                <h2 className="font-serif text-2xl text-[#F8F6F0] flex items-center gap-3">
                  <LayoutGrid size={20} className="text-gold" /> Layout &amp; floor-plan notes
                </h2>
                <p className="mt-4 text-sm text-zinc-300 leading-relaxed whitespace-pre-line">
                  {listing.floor_plan || "Detailed floor plans are shared during your virtual viewing — our advisor walks you through room dimensions, orientation and finish levels live on Zoom."}
                </p>
                <p className="mt-5 text-[11px] text-zinc-600 leading-relaxed">
                  Measurements are indicative; always confirm against the developer's registered floor plan before signing Form F.
                </p>
              </div>
              <MortgageCalculator price={listing.price_aed} />
            </div>
          </motion.div>
        )}
      </div>
      <Footer />
      {viewing && listing && <ViewingModal listing={listing} onClose={() => setViewing(false)} />}
    </main>
  );
}
